// Lógica PURA de «Mis clientes»: agrupar la cartera por cliente, filtrarla, contarla y decidir qué
// dice cada chip. Sin React y sin fetch — es lo que cubre `logic.test.ts`.
import dayjs from 'dayjs';
import { daysBetweenDays, formatShortDayWithYearMx } from '@/lib/dateHelpers';
import type { StatsBarItem, StatsBarTone } from '@/shared/patterns/StatsBar';
import type { CustomersFixture } from './fixtures';
import {
    ANY_FILTER,
    CUSTOMER_STAT_KEYS,
    CUSTOMER_STAT_LABEL,
    NO_TIER_FILTER,
    PAYMENT_CHANNEL_LABEL,
    type CollectorCustomerRow,
    type CustomerContact,
    type CustomerStatKey,
    type CustomerStats,
    type CustomerTier,
    type CustomersFilters,
    type TierFilterValue,
} from './types';

/** Lo que se pinta donde no hay dato. Nunca un cero: un ausente y un cero no son lo mismo. */
export const NO_VALUE = '—';

/** Días de «nunca se le contactó». Es el centinela del mockup, no una cuenta real. */
export const NO_CONTACT_DAYS = 999;

/** Cuántos contactos muestra la pestaña, como el mockup. */
export const CONTACTS_SHOWN = 12;

const MXN = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' });

/** Los importes viajan en centavos enteros; a pesos se dividen entre 100 antes de formatear. */
export function formatMoneyCents(cents: number): string {
    return MXN.format(cents / 100);
}

/** El día `YYYY-MM-DD` que resulta de correr `days` días. Negativo va hacia atrás. */
export function addDays(day: string, days: number): string {
    return dayjs(day).add(days, 'day').format('YYYY-MM-DD');
}

/** El día al que pertenece un sello del fixture (`YYYY-MM-DDTHH:mm`). */
export function dayOf(at: string): string {
    return at.slice(0, 10);
}

/** Cuántos días pasaron desde el último contacto. Sin contacto devuelve el centinela. */
function daysWithoutContactOf(lastContact: CustomerContact | null, today: string): number {
    if (lastContact === null) return NO_CONTACT_DAYS;
    return daysBetweenDays(today, dayOf(lastContact.at));
}

/**
 * Intentos de las últimas 48 h: los que no encontraron a nadie. Un SMS enviado no cuenta como
 * intento —sale solo—, pero un chat o un WhatsApp sin respuesta sí.
 */
function countRecentAttempts(contacts: CustomerContact[], today: string): number {
    const since = addDays(today, -2);
    return contacts.filter((contact) => {
        if (dayOf(contact.at) < since) return false;
        if (contact.outcome === 'no_answer' || contact.outcome === 'voicemail') return true;
        return contact.outcome === 'sent' && contact.channel !== 'sms';
    }).length;
}

/** Agrupa la cartera del cobrador por cliente y le cuelga sus derivados de cobranza. */
export function buildCustomerRows(fixture: CustomersFixture): CollectorCustomerRow[] {
    const { today } = fixture;

    return fixture.customers.map((customer) => {
        const credits = fixture.credits.filter((credit) => credit.customerId === customer.id);
        const creditIds = new Set(credits.map((credit) => credit.id));

        const promises = fixture.promises
            .filter((promise) => promise.customerId === customer.id)
            .sort((a, b) => b.day.localeCompare(a.day));
        const activePromises = promises.filter((promise) => promise.status === 'active');
        const brokenPromises = promises.filter((promise) => promise.status === 'broken');

        const contacts = fixture.contacts
            .filter((contact) => creditIds.has(contact.creditId))
            .sort((a, b) => b.at.localeCompare(a.at));
        const lastContact = contacts[0] ?? null;

        const payments = fixture.payments
            .filter((payment) => payment.customerId === customer.id)
            .sort((a, b) => b.at.localeCompare(a.at));

        return {
            id: customer.id,
            name: customer.name,
            phone: customer.phone,
            curp: customer.curp,
            tier: customer.tier,
            monthlyIncomeCents: customer.monthlyIncomeCents,
            altScore: customer.altScore,
            credits,
            worstArrearsDays: credits.reduce(
                (worst, credit) => Math.max(worst, credit.arrearsDays),
                0,
            ),
            totalArrearsCents: credits.reduce((sum, credit) => sum + credit.arrearsCents, 0),
            activePromise: activePromises[0] ?? null,
            brokenPromise: brokenPromises[0] ?? null,
            hasPromiseToday: activePromises.some((promise) => promise.day === today),
            hasPromiseTomorrow: activePromises.some((promise) => promise.day === addDays(today, 1)),
            lastContact,
            daysWithoutContact: daysWithoutContactOf(lastContact, today),
            attempts48h: countRecentAttempts(contacts, today),
            promises,
            contacts,
            payments,
        };
    });
}

/** El nivel tal como lo nombra el chip del filtro. `null` es la opción «Sin». */
function tierFilterValueOf(tier: CustomerTier): TierFilterValue {
    return tier ?? NO_TIER_FILTER;
}

function matchesSearch(row: CollectorCustomerRow, search: string): boolean {
    const needle = search.trim().toLowerCase();
    if (needle === '') return true;
    const haystack = [row.name, row.id, row.phone ?? '', row.curp ?? ''].join(' ').toLowerCase();
    return haystack.includes(needle);
}

function matchesArrears(row: CollectorCustomerRow, filter: CustomersFilters['arrears']): boolean {
    const days = row.worstArrearsDays;
    switch (filter) {
        case ANY_FILTER:
            return true;
        case 'current':
            return days <= 0;
        case '1-7':
            return days >= 1 && days <= 7;
        case '8-30':
            return days >= 8 && days <= 30;
        case '31-60':
            return days >= 31 && days <= 60;
        case '61-90':
            return days >= 61 && days <= 90;
        case '90+':
            return days >= 90;
    }
}

function matchesPromise(row: CollectorCustomerRow, filter: CustomersFilters['promise']): boolean {
    switch (filter) {
        case ANY_FILTER:
            return true;
        case 'today':
            return row.hasPromiseToday;
        case 'tomorrow':
            return row.hasPromiseTomorrow;
        case 'active':
            return row.activePromise !== null;
        case 'broken':
            return row.brokenPromise !== null;
        case 'none':
            return row.activePromise === null && row.brokenPromise === null;
    }
}

function matchesContact(row: CollectorCustomerRow, filter: CustomersFilters['contact']): boolean {
    const days = row.daysWithoutContact;
    switch (filter) {
        case ANY_FILTER:
            return true;
        case 'today':
            return days === 0;
        case '24h':
            return days >= 1;
        case '3d':
            return days >= 3;
        case '7d':
            return days >= 7;
    }
}

/** Los cinco grupos de la columna de filtros. Cada grupo recorta; entre chips de Nivel es un O. */
export function filterCustomers(
    rows: readonly CollectorCustomerRow[],
    filters: CustomersFilters,
): CollectorCustomerRow[] {
    return rows.filter((row) => {
        if (filters.tiers.length > 0 && !filters.tiers.includes(tierFilterValueOf(row.tier))) {
            return false;
        }
        if (!matchesArrears(row, filters.arrears)) return false;
        if (!matchesPromise(row, filters.promise)) return false;
        if (!matchesContact(row, filters.contact)) return false;
        return matchesSearch(row, filters.search);
    });
}

/** Cuántos filtros están puestos: cada chip de Nivel cuenta aparte, como en el Admin VL. */
export function countActiveFilters(filters: CustomersFilters): number {
    let count = filters.tiers.length;
    if (filters.search.trim() !== '') count += 1;
    if (filters.arrears !== ANY_FILTER) count += 1;
    if (filters.promise !== ANY_FILTER) count += 1;
    if (filters.contact !== ANY_FILTER) count += 1;
    return count;
}

/** La pastilla activa de la barra de KPIs también recorta la lista. */
export function applyStatFilter(
    rows: readonly CollectorCustomerRow[],
    stat: CustomerStatKey,
): CollectorCustomerRow[] {
    switch (stat) {
        case 'all':
            return [...rows];
        case 'arrears':
            return rows.filter((row) => row.worstArrearsDays > 0);
        case 'current':
            return rows.filter((row) => row.worstArrearsDays <= 0);
        case 'promiseToday':
            return rows.filter((row) => row.hasPromiseToday);
        case 'noContact':
            return rows.filter((row) => row.daysWithoutContact >= 3);
    }
}

/** El orden del mockup: la peor mora arriba, que es por donde se empieza el turno. */
export function sortCustomers(rows: readonly CollectorCustomerRow[]): CollectorCustomerRow[] {
    return [...rows].sort((a, b) => b.worstArrearsDays - a.worstArrearsDays);
}

/** Los conteos de las cinco pastillas. Cuentan sobre TODA la cartera, no sobre lo filtrado. */
export function computeCustomerStats(rows: readonly CollectorCustomerRow[]): CustomerStats {
    return {
        all: rows.length,
        arrears: rows.filter((row) => row.worstArrearsDays > 0).length,
        current: rows.filter((row) => row.worstArrearsDays <= 0).length,
        promiseToday: rows.filter((row) => row.hasPromiseToday).length,
        noContact: rows.filter((row) => row.daysWithoutContact >= 3).length,
    };
}

/** El color con el que el mockup destaca el número de cada pastilla. */
const CUSTOMER_STAT_TONE: Record<CustomerStatKey, StatsBarTone> = {
    all: 'neutral',
    arrears: 'danger',
    current: 'success',
    promiseToday: 'info',
    noContact: 'warning',
};

/** Las 5 pastillas de «Mis clientes» para la `<StatsBar>`, en el orden del mockup. */
export function customersStatsItems(stats: CustomerStats): StatsBarItem<CustomerStatKey>[] {
    return CUSTOMER_STAT_KEYS.map((key) => ({
        key,
        label: CUSTOMER_STAT_LABEL[key],
        value: stats[key],
        tone: CUSTOMER_STAT_TONE[key],
    }));
}

/** El «Cartera total» del pie de la barra: suma la mora de las filas VISIBLES. */
export function sumArrearsCents(rows: readonly CollectorCustomerRow[]): number {
    return rows.reduce((sum, row) => sum + row.totalArrearsCents, 0);
}

export type CobranzaTone = 'success' | 'danger' | 'warning' | 'info' | 'muted' | 'primary';

/** Cómo se pinta la mora: al día en verde, y a más días, más caliente. */
export function arrearsTone(days: number): CobranzaTone {
    if (days <= 0) return 'success';
    if (days > 30) return 'danger';
    if (days > 7) return 'warning';
    return 'info';
}

export function arrearsLabel(days: number): string {
    return days <= 0 ? 'Al día' : `${String(days)}d`;
}

/** Qué dice la columna Compromiso. `null` = no hay ninguno y va el guion. */
export function promiseCell(
    row: CollectorCustomerRow,
): { label: string; tone: CobranzaTone } | null {
    if (row.hasPromiseToday) return { label: 'Hoy', tone: 'info' };
    if (row.hasPromiseTomorrow) return { label: 'Mañana', tone: 'primary' };
    if (row.brokenPromise !== null) return { label: 'Incumpl.', tone: 'danger' };
    if (row.activePromise !== null) {
        return { label: formatFixtureDay(row.activePromise.day), tone: 'muted' };
    }
    return null;
}

/** Qué dice la columna «Últ. cont.»: hoy, ayer, o los días que lleva sin que nadie lo alcance. */
export function lastContactCell(row: CollectorCustomerRow): { label: string; tone: CobranzaTone } {
    if (row.lastContact === null) return { label: 'Sin contacto', tone: 'warning' };
    const days = row.daysWithoutContact;
    if (days === 0) return { label: 'hoy', tone: 'success' };
    if (days === 1) return { label: 'ayer', tone: 'muted' };
    return { label: `${String(days)}d`, tone: days <= 3 ? 'info' : 'warning' };
}

/** El sello corto del mockup (`06 jul 26`) para un día o un instante del fixture. */
export function formatFixtureDay(value: string): string {
    return formatShortDayWithYearMx(dayOf(value));
}

/** Cómo resume la ficha los compromisos del cliente. */
export function promisesSummary(row: CollectorCustomerRow): string {
    const parts = [
        row.activePromise === null ? null : `1 activo (${formatFixtureDay(row.activePromise.day)})`,
        row.brokenPromise === null ? null : '1 incumplido',
    ].filter((part): part is string => part !== null);
    return parts.length === 0 ? 'Sin compromisos' : parts.join(' · ');
}

/**
 * La sugerencia del día: el texto que el mockup deriva de la mora y del compromiso. Es copy, no
 * regla de negocio — el orden de los casos es el que decide cuál se lee.
 */
export function dailySuggestion(row: CollectorCustomerRow): string {
    if (row.brokenPromise !== null) {
        return 'Compromiso incumplido. Prioridad máxima: contactar antes de 24 h o escalar al supervisor.';
    }
    if (row.hasPromiseToday && row.activePromise !== null) {
        const amount = formatMoneyCents(row.activePromise.amountCents);
        const channel = PAYMENT_CHANNEL_LABEL[row.activePromise.channel];
        return `Vence compromiso hoy · ${amount} por ${channel}. Confirma con el cliente por la mañana.`;
    }
    if (row.daysWithoutContact >= 3) {
        return `Sin contacto ${String(row.daysWithoutContact)} días. Intenta canal alterno (WhatsApp) o contacto de referencia.`;
    }
    if (row.worstArrearsDays > 30) {
        return 'Mora > 30 días. Considera proponer reestructura desde el drawer "Más" del caso.';
    }
    if (row.worstArrearsDays > 0) {
        return 'Registra un compromiso concreto (fecha + canal). El cure rate del portal sube 25% con compromisos vs sin ellos.';
    }
    return 'Cliente al día. Revisa próxima cuota en agenda semanal para anticiparte.';
}

/** Las iniciales del avatar: las dos primeras palabras del nombre. */
export function initialsOf(name: string): string {
    return name
        .split(' ')
        .filter((part) => part !== '')
        .slice(0, 2)
        .map((part) => part[0])
        .join('')
        .toUpperCase();
}
